import { getFolders } from '../collectionFolders';
import { normalizeFolderVariables } from '../folderVariables';
import {
  mapRequestToPostmanItem,
  variablesToPostman,
  variableRowsToPostman,
  collectionAuthToPostman,
} from './postmanMappers';

function buildFolderTree(folders, parentId, requestsByFolder) {
  const children = folders
    .filter((f) => (f.parent_id ?? null) === parentId)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const items = [];

  for (const folder of children) {
    const nested = buildFolderTree(folders, folder.id, requestsByFolder);
    const folderRequests = (requestsByFolder.get(folder.id) || [])
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((r) => r.item);

    const group = {
      name: folder.name || 'Folder',
      item: [...nested, ...folderRequests],
    };
    // v2.1 item-groups carry `variable`; legacy rows without `enabled` are
    // enabled. A SECRET folder row is exported without its value and typed
    // `secret` (Postman's own marker), so the file never carries it (tester F1).
    const variable = variableRowsToPostman(normalizeFolderVariables(folder.variables));
    if (variable.length) group.variable = variable;
    items.push(group);
  }

  return items;
}

/**
 * @param {object} collection Sidebar collection
 * @param {{ push: (msg: string) => void }} warnings
 */
export function toPostmanCollection(collection, warnings) {
  const folders = getFolders(collection);
  const requests = collection.requests || [];

  const requestsByFolder = new Map();
  const rootRequests = [];

  for (const req of requests) {
    const item = mapRequestToPostmanItem(req, warnings);
    const folderId = req.folder_id ?? req.settings?.folder_id ?? null;
    if (folderId) {
      if (!requestsByFolder.has(folderId)) {
        requestsByFolder.set(folderId, []);
      }
      requestsByFolder.get(folderId).push({ sort_order: req.sort_order, item });
    } else {
      rootRequests.push({ sort_order: req.sort_order, item });
    }
  }

  const folderItems = buildFolderTree(folders, null, requestsByFolder);
  const rootItems = rootRequests
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((r) => r.item);

  const variables = variablesToPostman(collection.settings);
  const auth = collectionAuthToPostman(collection.settings, warnings);

  const result = {
    info: {
      name: collection.name || 'Collection',
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      description: collection.description || '',
    },
    item: [...folderItems, ...rootItems],
  };

  if (variables.length) {
    result.variable = variables;
  }
  if (auth) {
    result.auth = auth;
  }

  return result;
}
